import {
    CustomerServiceHeaderIcon,
    DataScienceHeaderIcon,
    DesignHeaderIcon,
    FinanceHeaderIcon,
    HumanResourcesHeaderIcon,
    ITSupportHeaderIcon,
    LegalHeaderIcon,
    MarketingHeaderIcon,
    OperationsHeaderIcon,
    OtherHeaderIcon,
    ProductManagementHeaderIcon,
    ProjectManagementHeaderIcon,
    SalesHeaderIcon,
    SoftwareDevelopmentHeaderIcon
} from "../views/user-seg-bxp-ui/custom-icon";

export const ALL_ROLES_ELIGIBLE = "all-roles-eligible";

export type UserAccountData = {
    teamType: string;
    role: string;
};

export type UserSegmentData = {
    userAccountData: UserAccountData | null;
    isBusinessSegmentUser: boolean;
    fetchError: boolean;
    fetched: boolean;
    teamType: UserSegmentationTeamTypes | null;
};

export const USER_SEGMENTATION_MAPPING_UNKNOWN = "unknown";
export const USER_SEGMENTATION_MAPPING_NONE = "";

// Roles defined in user segmentation
const USER_SEGMENTATION_ROLE_DESIGNER = "designer";

// Team types defined in user segmentation
export const USER_SEGMENTATION_TEAM_TYPE_CUSTOMER_SERVICE = "customer-service";
export const USER_SEGMENTATION_TEAM_TYPE_IT_SUPPORT = "it-support";
export const USER_SEGMENTATION_TEAM_TYPE_HR = "human-resources";
export const USER_SEGMENTATION_TEAM_TYPE_FINANCE = "finance";
export const USER_SEGMENTATION_TEAM_TYPE_LEGAL = "legal";
export const USER_SEGMENTATION_TEAM_TYPE_MARKETING = "marketing";
export const USER_SEGMENTATION_TEAM_TYPE_OPERATIONS = "operations";
export const USER_SEGMENTATION_TEAM_TYPE_SALES = "sales";
export const USER_SEGMENTATION_TEAM_TYPE_DATA_SCIENCE = "data-science";
export const USER_SEGMENTATION_TEAM_TYPE_SOFTWARE = "software-development";
export const USER_SEGMENTATION_TEAM_TYPE_PRODUCT_MANAGEMENT =
    "product-management";
export const USER_SEGMENTATION_TEAM_TYPE_PROJECT_MANAGEMENT =
    "project-management";
export const USER_SEGMENTATION_TEAM_TYPE_DESIGN = "design";
export const USER_SEGMENTATION_TEAM_TYPE_OTHER_PERSONAL = "other-personal";
export const USER_SEGMENTATION_TEAM_TYPE_SKIP = "skip";

export const wacToIdentityUserSegMap = {
    [USER_SEGMENTATION_TEAM_TYPE_SOFTWARE]: "Software Development",
    [USER_SEGMENTATION_TEAM_TYPE_PRODUCT_MANAGEMENT]: "Product Management",
    [USER_SEGMENTATION_TEAM_TYPE_PROJECT_MANAGEMENT]: "Project Management",
    [USER_SEGMENTATION_TEAM_TYPE_IT_SUPPORT]: "IT Support",
    [USER_SEGMENTATION_TEAM_TYPE_DESIGN]: "Design",
    [USER_SEGMENTATION_TEAM_TYPE_CUSTOMER_SERVICE]: "Customer Service",
    [USER_SEGMENTATION_TEAM_TYPE_HR]: "Human Resources",
    [USER_SEGMENTATION_TEAM_TYPE_MARKETING]: "Marketing",
    [USER_SEGMENTATION_TEAM_TYPE_OPERATIONS]: "Operations",
    [USER_SEGMENTATION_TEAM_TYPE_SALES]: "Sales",
    [USER_SEGMENTATION_TEAM_TYPE_DATA_SCIENCE]: "Data Science",
    [USER_SEGMENTATION_TEAM_TYPE_FINANCE]: "Finance",
    [USER_SEGMENTATION_TEAM_TYPE_LEGAL]: "Legal",
    [USER_SEGMENTATION_TEAM_TYPE_OTHER_PERSONAL]: "Other or Personal",
    [USER_SEGMENTATION_TEAM_TYPE_SKIP]: "Skip"
};

export type UserSegmentationTeamType = keyof typeof wacToIdentityUserSegMap;

export const getWacTeamTypeFromIdentity = (teamType: string) => {
    for (const [key, value] of Object.entries(wacToIdentityUserSegMap)) {
        if (value === teamType) {
            return key;
        }
    }

    return undefined;
};

export const productQueryParamTeamTypeMap = {
    [USER_SEGMENTATION_TEAM_TYPE_SOFTWARE]: "DEV",
    [USER_SEGMENTATION_TEAM_TYPE_IT_SUPPORT]: "ITS",
    [USER_SEGMENTATION_TEAM_TYPE_DESIGN]: "DES",
    [USER_SEGMENTATION_TEAM_TYPE_CUSTOMER_SERVICE]: "CS",
    [USER_SEGMENTATION_TEAM_TYPE_HR]: "HR",
    [USER_SEGMENTATION_TEAM_TYPE_MARKETING]: "MKT",
    [USER_SEGMENTATION_TEAM_TYPE_OPERATIONS]: "OPS",
    [USER_SEGMENTATION_TEAM_TYPE_SALES]: "SAL",
    [USER_SEGMENTATION_TEAM_TYPE_FINANCE]: "FNC",
    [USER_SEGMENTATION_TEAM_TYPE_LEGAL]: "LGL",
    [USER_SEGMENTATION_TEAM_TYPE_OTHER_PERSONAL]: "OTR",
    [USER_SEGMENTATION_TEAM_TYPE_PRODUCT_MANAGEMENT]: "PDM",
    [USER_SEGMENTATION_TEAM_TYPE_PROJECT_MANAGEMENT]: "PJM",
    [USER_SEGMENTATION_TEAM_TYPE_DATA_SCIENCE]: "DS"
};

export const getProductQueryParamTeamType = (teamType: string) => {
    for (const [key, value] of Object.entries(productQueryParamTeamTypeMap)) {
        if (key === teamType) {
            return value;
        }
    }
    return teamType;
};

// eslint-disable-next-line complexity
export const getIdentityTeamType = (teamType: string) => {
    switch (teamType) {
        case USER_SEGMENTATION_TEAM_TYPE_SOFTWARE:
            return "Software Development";
        case USER_SEGMENTATION_TEAM_TYPE_IT_SUPPORT:
            return "IT Support";
        case USER_SEGMENTATION_TEAM_TYPE_DESIGN:
            return "Design";
        case USER_SEGMENTATION_TEAM_TYPE_CUSTOMER_SERVICE:
            return "Customer Service";
        case USER_SEGMENTATION_TEAM_TYPE_HR:
            return "Human Resources";
        case USER_SEGMENTATION_TEAM_TYPE_MARKETING:
            return "Marketing";
        case USER_SEGMENTATION_TEAM_TYPE_OPERATIONS:
            return "Operations";
        case USER_SEGMENTATION_TEAM_TYPE_SALES:
            return "Sales";
        case USER_SEGMENTATION_TEAM_TYPE_FINANCE:
            return "Finance";
        case USER_SEGMENTATION_TEAM_TYPE_LEGAL:
            return "Legal";
        case USER_SEGMENTATION_TEAM_TYPE_OTHER_PERSONAL:
            return "Other or Personal";
        case USER_SEGMENTATION_TEAM_TYPE_PRODUCT_MANAGEMENT:
            return "Product Management";
        case USER_SEGMENTATION_TEAM_TYPE_PROJECT_MANAGEMENT:
            return "Project Management";
        case USER_SEGMENTATION_TEAM_TYPE_DATA_SCIENCE:
            return "Data Science";
        case USER_SEGMENTATION_TEAM_TYPE_SKIP:
            return "Skip";
        default:
            return teamType;
    }
};

export const getIconForTransparentTemplateV2 = (teamType: string) => {
    switch (teamType) {
        case USER_SEGMENTATION_TEAM_TYPE_SOFTWARE:
            return SoftwareDevelopmentHeaderIcon;
        case USER_SEGMENTATION_TEAM_TYPE_IT_SUPPORT:
            return ITSupportHeaderIcon;
        case USER_SEGMENTATION_TEAM_TYPE_DESIGN:
            return DesignHeaderIcon;
        case USER_SEGMENTATION_TEAM_TYPE_CUSTOMER_SERVICE:
            return CustomerServiceHeaderIcon;
        case USER_SEGMENTATION_TEAM_TYPE_HR:
            return HumanResourcesHeaderIcon;
        case USER_SEGMENTATION_TEAM_TYPE_MARKETING:
            return MarketingHeaderIcon;
        case USER_SEGMENTATION_TEAM_TYPE_OPERATIONS:
            return OperationsHeaderIcon;
        case USER_SEGMENTATION_TEAM_TYPE_SALES:
            return SalesHeaderIcon;
        case USER_SEGMENTATION_TEAM_TYPE_FINANCE:
            return FinanceHeaderIcon;
        case USER_SEGMENTATION_TEAM_TYPE_LEGAL:
            return LegalHeaderIcon;
        case USER_SEGMENTATION_TEAM_TYPE_OTHER_PERSONAL:
            return OtherHeaderIcon;
        case USER_SEGMENTATION_TEAM_TYPE_PRODUCT_MANAGEMENT:
            return ProductManagementHeaderIcon;
        case USER_SEGMENTATION_TEAM_TYPE_PROJECT_MANAGEMENT:
            return ProjectManagementHeaderIcon;
        case USER_SEGMENTATION_TEAM_TYPE_DATA_SCIENCE:
            return DataScienceHeaderIcon;
        default:
            return null;
    }
};

export const getTeamTypeForTransparentTemplateV2 = (
    teamType: string,
    formatMessage: (parameters: {
        id: string;
        defaultMessage: string;
    }) => string
) => {
    switch (teamType) {
        case USER_SEGMENTATION_TEAM_TYPE_SOFTWARE:
            return formatMessage({
                id: "UserSeg.teamTypeSelection.softwareDevelopment",
                defaultMessage: "Software development"
            });
        case USER_SEGMENTATION_TEAM_TYPE_IT_SUPPORT:
            return formatMessage({
                id: "UserSeg.teamTypeSelection.itSupport",
                defaultMessage: "IT support"
            });
        case USER_SEGMENTATION_TEAM_TYPE_DESIGN:
            return formatMessage({
                id: "UserSeg.teamTypeSelection.design",
                defaultMessage: "Design"
            });
        case USER_SEGMENTATION_TEAM_TYPE_CUSTOMER_SERVICE:
            return formatMessage({
                id: "UserSeg.teamTypeSelection.customerService",
                defaultMessage: "Customer service"
            });
        case USER_SEGMENTATION_TEAM_TYPE_HR:
            return formatMessage({
                id: "UserSeg.teamTypeSelection.humanResources",
                defaultMessage: "Human resources"
            });
        case USER_SEGMENTATION_TEAM_TYPE_MARKETING:
            return formatMessage({
                id: "UserSeg.teamTypeSelection.marketing",
                defaultMessage: "Marketing"
            });
        case USER_SEGMENTATION_TEAM_TYPE_OPERATIONS:
            return formatMessage({
                id: "UserSeg.teamTypeSelection.operations",
                defaultMessage: "Operations"
            });
        case USER_SEGMENTATION_TEAM_TYPE_SALES:
            return formatMessage({
                id: "UserSeg.teamTypeSelection.sales",
                defaultMessage: "Sales"
            });
        case USER_SEGMENTATION_TEAM_TYPE_FINANCE:
            return formatMessage({
                id: "UserSeg.teamTypeSelection.finance",
                defaultMessage: "Finance"
            });
        case USER_SEGMENTATION_TEAM_TYPE_LEGAL:
            return formatMessage({
                id: "UserSeg.teamTypeSelection.legal",
                defaultMessage: "Legal"
            });
        case USER_SEGMENTATION_TEAM_TYPE_OTHER_PERSONAL:
            return formatMessage({
                id: "UserSeg.teamTypeSelection.otherOrPersonal",
                defaultMessage: "Other"
            });
        case USER_SEGMENTATION_TEAM_TYPE_PRODUCT_MANAGEMENT:
            return formatMessage({
                id: "UserSeg.teamTypeSelection.productManagement",
                defaultMessage: "Product management"
            });
        case USER_SEGMENTATION_TEAM_TYPE_PROJECT_MANAGEMENT:
            return formatMessage({
                id: "UserSeg.teamTypeSelection.projectManagement",
                defaultMessage: "Project management"
            });
        case USER_SEGMENTATION_TEAM_TYPE_DATA_SCIENCE:
            return formatMessage({
                id: "UserSeg.teamTypeSelection.dataScience",
                defaultMessage: "Data science"
            });
        default:
            return teamType;
    }
};

export const BUSINESS_ROLES_PER_TEAM_TYPE: Partial<
    Record<UserSegmentationTeamTypes, UserSegmentationRoleTypes[]>
> = {
    [USER_SEGMENTATION_TEAM_TYPE_HR]: [ALL_ROLES_ELIGIBLE],
    [USER_SEGMENTATION_TEAM_TYPE_FINANCE]: [ALL_ROLES_ELIGIBLE],
    [USER_SEGMENTATION_TEAM_TYPE_LEGAL]: [ALL_ROLES_ELIGIBLE],
    [USER_SEGMENTATION_TEAM_TYPE_MARKETING]: [ALL_ROLES_ELIGIBLE],
    [USER_SEGMENTATION_TEAM_TYPE_OPERATIONS]: [ALL_ROLES_ELIGIBLE],
    [USER_SEGMENTATION_TEAM_TYPE_SALES]: [ALL_ROLES_ELIGIBLE],
    [USER_SEGMENTATION_TEAM_TYPE_SOFTWARE]: [USER_SEGMENTATION_ROLE_DESIGNER]
};

export const BUSINESS_TEAM_TYPES: UserSegmentationTeamTypes[] = [
    USER_SEGMENTATION_TEAM_TYPE_HR,
    USER_SEGMENTATION_TEAM_TYPE_FINANCE,
    USER_SEGMENTATION_TEAM_TYPE_LEGAL,
    USER_SEGMENTATION_TEAM_TYPE_MARKETING,
    USER_SEGMENTATION_TEAM_TYPE_OPERATIONS,
    USER_SEGMENTATION_TEAM_TYPE_SALES,
    USER_SEGMENTATION_TEAM_TYPE_SOFTWARE
];

export type UserSegmentationRoleTypes =
    | typeof USER_SEGMENTATION_ROLE_DESIGNER
    | typeof ALL_ROLES_ELIGIBLE;

export type UserSegmentationTeamTypes =
    | typeof USER_SEGMENTATION_TEAM_TYPE_CUSTOMER_SERVICE
    | typeof USER_SEGMENTATION_TEAM_TYPE_IT_SUPPORT
    | typeof USER_SEGMENTATION_TEAM_TYPE_HR
    | typeof USER_SEGMENTATION_TEAM_TYPE_FINANCE
    | typeof USER_SEGMENTATION_TEAM_TYPE_LEGAL
    | typeof USER_SEGMENTATION_TEAM_TYPE_MARKETING
    | typeof USER_SEGMENTATION_TEAM_TYPE_OPERATIONS
    | typeof USER_SEGMENTATION_TEAM_TYPE_SALES
    | typeof USER_SEGMENTATION_TEAM_TYPE_SOFTWARE
    | typeof USER_SEGMENTATION_TEAM_TYPE_DESIGN
    | typeof USER_SEGMENTATION_TEAM_TYPE_OTHER_PERSONAL
    | typeof USER_SEGMENTATION_TEAM_TYPE_PRODUCT_MANAGEMENT
    | typeof USER_SEGMENTATION_TEAM_TYPE_PROJECT_MANAGEMENT
    | typeof USER_SEGMENTATION_TEAM_TYPE_DATA_SCIENCE
    | typeof USER_SEGMENTATION_TEAM_TYPE_SKIP;

export const USER_SEGMENTATION_TEAM_TYPES = [
    USER_SEGMENTATION_TEAM_TYPE_CUSTOMER_SERVICE,
    USER_SEGMENTATION_TEAM_TYPE_IT_SUPPORT,
    USER_SEGMENTATION_TEAM_TYPE_HR,
    USER_SEGMENTATION_TEAM_TYPE_FINANCE,
    USER_SEGMENTATION_TEAM_TYPE_LEGAL,
    USER_SEGMENTATION_TEAM_TYPE_MARKETING,
    USER_SEGMENTATION_TEAM_TYPE_OPERATIONS,
    USER_SEGMENTATION_TEAM_TYPE_SALES,
    USER_SEGMENTATION_TEAM_TYPE_SOFTWARE,
    USER_SEGMENTATION_TEAM_TYPE_DESIGN,
    USER_SEGMENTATION_TEAM_TYPE_PRODUCT_MANAGEMENT,
    USER_SEGMENTATION_TEAM_TYPE_PROJECT_MANAGEMENT,
    USER_SEGMENTATION_TEAM_TYPE_DATA_SCIENCE,
    USER_SEGMENTATION_TEAM_TYPE_OTHER_PERSONAL,
    USER_SEGMENTATION_TEAM_TYPE_SKIP
];

type UserSegmentationMapping =
    | UserSegmentationTeamTypes
    | UserSegmentationRoleTypes;

// @ts-expect-error - It seems to be missing some translations (for all-roles-eligible).
export const USER_SEGMENTATION_CLIENT_MAPPING: Record<
    UserSegmentationMapping,
    string[]
> = {
    [USER_SEGMENTATION_TEAM_TYPE_PROJECT_MANAGEMENT]: ["Project Management"],
    [USER_SEGMENTATION_TEAM_TYPE_PRODUCT_MANAGEMENT]: ["Product Management"],
    [USER_SEGMENTATION_TEAM_TYPE_DATA_SCIENCE]: ["Data Science"],
    [USER_SEGMENTATION_TEAM_TYPE_OTHER_PERSONAL]: ["Other or Personal"],
    [USER_SEGMENTATION_ROLE_DESIGNER]: [
        "Designer",
        "Disainer",
        "Diseñador",
        "Formgivare",
        "Nhà thiết kế",
        "Projektant",
        "Suunnittelija",
        "Tasarımcı",
        "Tervező",
        "Дизайнер",
        "นักออกแบบ",
        "デザイナー",
        "設計師",
        "设计师",
        "디자이너"
    ],
    [USER_SEGMENTATION_TEAM_TYPE_DESIGN]: ["Design"],
    [USER_SEGMENTATION_TEAM_TYPE_CUSTOMER_SERVICE]: [
        "Asiakaspalvelu",
        "Atendimento ao cliente",
        "Customer Service",
        "Dịch vụ khách hàng",
        "Klantenservice",
        "Klienditeenindus",
        "Kundenservice",
        "Kundeservice",
        "Kundservice",
        "Müşteri Hizmetleri",
        "Obsługa klienta",
        "Service à la clientèle",
        "Servicio de atención al cliente",
        "Servizio clienti",
        "Serviço de apoio ao cliente",
        "Zákaznický servis",
        "Zákaznícky servis",
        "Ügyfélszolgálati",
        "Обслуживание клиентов",
        "Служба підтримки клієнтів",
        "บริการลูกค้า",
        "カスタマー サービス",
        "客户服务",
        "顧客服務",
        "고객 서비스"
    ],
    [USER_SEGMENTATION_TEAM_TYPE_IT_SUPPORT]: [
        "Assistenza IT",
        "BT Desteği",
        "Hỗ trợ CNTT",
        "IT",
        "IT Support",
        "IT podpora",
        "IT サポート",
        "IT 支持",
        "IT 支援",
        "IT 지원",
        "IT_Support",
        "IT_støtte",
        "IT_support",
        "IT_tugi",
        "IT_tuki",
        "Informatikai támogatási",
        "Soporte de TI",
        "Suporte de TI",
        "Support IT",
        "Wsparcie IT",
        "ИТ-поддержка",
        "Служба технічної підтримки",
        "ฝ่ายสนับสนุน IT"
    ],
    [USER_SEGMENTATION_TEAM_TYPE_FINANCE]: [
        "Ekonomi",
        "Finance",
        "Financeiro",
        "Financie",
        "Finans",
        "Finanse",
        "Finants",
        "Finanza",
        "Finanzas",
        "Finanzen",
        "Finanças",
        "Pénzügyi",
        "Rahoitus",
        "Tài chính",
        "Økonomi",
        "Финансы",
        "Фінанси",
        "การเงิน",
        "財務",
        "金融",
        "재무"
    ],
    [USER_SEGMENTATION_TEAM_TYPE_HR]: [
        "HR",
        "Henkilöstöhallinto",
        "Human Resources",
        "Human resources",
        "human-resources", // for Upaas
        "Lidské zdroje",
        "Menneskelige ressourcer",
        "Nhân sự",
        "Personal",
        "Personalfrågor",
        "RH",
        "Recursos Humanos",
        "Recursos humanos",
        "Risorse umane",
        "Személyzeti",
        "Zasoby ludzkie",
        "İnsan Kaynakları",
        "Ľudské zdroje",
        "Відділ кадрів",
        "Кадровые вопросы",
        "ทรัพยากรบุคคล",
        "人事",
        "人力資源",
        "人力资源",
        "인사"
    ],
    [USER_SEGMENTATION_TEAM_TYPE_LEGAL]: [
        "Departamento jurídico",
        "Hukuk",
        "Jogi",
        "Jura",
        "Juridik",
        "Juridisk",
        "Jurídico",
        "Legal",
        "Legale",
        "Oikeudelliset asiat",
        "Pháp lý",
        "Prawo",
        "Právne oddelenie",
        "Právní oddělení",
        "Rechtliches",
        "Service juridique",
        "Õigus",
        "Юридические вопросы",
        "Юридичний відділ",
        "กฎหมาย",
        "法務",
        "法律",
        "법률"
    ],
    [USER_SEGMENTATION_TEAM_TYPE_MARKETING]: [
        "Markedsføring",
        "Marketing",
        "Markkinointi",
        "Marknadsföring",
        "Pazarlama",
        "Tiếp thị",
        "Turundus",
        "Маркетинг",
        "การตลาด",
        "マーケティング",
        "营销",
        "行銷",
        "마케팅"
    ],
    [USER_SEGMENTATION_TEAM_TYPE_OPERATIONS]: [
        "Drift",
        "Haldus",
        "Operacional",
        "Operaciones",
        "Operacje",
        "Operasyonlar",
        "Operations",
        "Operativer Betrieb",
        "Operazioni",
        "Operações",
        "Opérations",
        "Prevádzka",
        "Provoz",
        "Toiminnot",
        "Vận hành",
        "Üzemeltetési",
        "Виробничий відділ",
        "Операционные вопросы",
        "ฝ่ายปฏิบัติการ",
        "オペレーション",
        "營運",
        "运营",
        "운영"
    ],
    [USER_SEGMENTATION_TEAM_TYPE_SALES]: [
        "Bán hàng",
        "Försäljning",
        "Myynti",
        "Müük",
        "Predaj",
        "Prodej",
        "Sales",
        "Salg",
        "Satış",
        "Sprzedaż",
        "Vendas",
        "Vendite",
        "Ventas",
        "Ventes",
        "Vertrieb",
        "Értékesítési",
        "Продажи",
        "Продажі",
        "ฝ่ายขาย",
        "セールス",
        "銷售",
        "销售",
        "영업"
    ],
    [USER_SEGMENTATION_TEAM_TYPE_SOFTWARE]: [
        "Desarrollo de software",
        "Desenvolvimento de software",
        "Développement logiciel",
        "Ohjelmistokehitys",
        "Phát triển phần mềm",
        "Programutveckling",
        "Programvareutvikling",
        "Software Development",
        "Softwareentwicklung",
        "Softwareontwikkeling",
        "Softwareudvikling",
        "Sviluppo software",
        "Szoftverfejlesztő",
        "Tarkvaraarendus",
        "Tworzenie oprogramowania",
        "Vývoj softvéru",
        "Vývoj softwaru",
        "Yazılım Geliştirme",
        "Разработка ПО",
        "Розробка ПЗ",
        "พัฒนาซอฟต์แวร์",
        "ソフトウェア開発",
        "軟體開發",
        "软件开发",
        "소프트웨어 개발"
    ]
};
